import { useEffect, useState } from "react";
import { MapContainer, TileLayer, Marker, useMapEvents, useMap } from "react-leaflet";
import L from "leaflet";
import { LocateFixed, MapPin, Link2 } from "lucide-react";
import { toast } from "sonner";

const icon = new L.Icon({
  iconUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png",
  iconRetinaUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png",
  shadowUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png",
  iconSize: [25, 41], iconAnchor: [12, 41], popupAnchor: [1, -34], shadowSize: [41, 41],
});

const MAGELANG = [-7.4699, 110.2177];

function ClickHandler({ onPick }) {
  useMapEvents({ click: (e) => onPick(e.latlng.lat, e.latlng.lng) });
  return null;
}

function Recenter({ position }) {
  const map = useMap();
  useEffect(() => { if (position) map.setView(position, Math.max(map.getZoom(), 15)); }, [position, map]);
  return null;
}

/**
 * Parse coordinates from a Google Maps URL. Supports the common desktop formats.
 * Short links (maps.app.goo.gl, goo.gl/maps) need to be expanded — we ask user to do so.
 */
function parseGoogleMapsUrl(url) {
  if (!url) return null;
  const patterns = [
    /@(-?\d+\.\d+),(-?\d+\.\d+)/,
    /[?&]q=(-?\d+\.\d+),(-?\d+\.\d+)/,
    /[?&]ll=(-?\d+\.\d+),(-?\d+\.\d+)/,
    /!3d(-?\d+\.\d+)!4d(-?\d+\.\d+)/,
  ];
  for (const rx of patterns) {
    const m = url.match(rx);
    if (m) return { lat: parseFloat(m[1]), lng: parseFloat(m[2]) };
  }
  return null;
}

export default function LocationPicker({ lat, lng, onChange }) {
  const hasCoords = lat !== "" && lng !== "" && lat != null && lng != null && !isNaN(parseFloat(lat)) && !isNaN(parseFloat(lng));
  const [urlInput, setUrlInput] = useState("");
  const position = hasCoords ? [parseFloat(lat), parseFloat(lng)] : null;

  const pick = (la, ln) => {
    onChange({ lat: Number(la).toFixed(6), lng: Number(ln).toFixed(6) });
  };

  const useMyLocation = () => {
    if (!navigator.geolocation) { toast.error("Geolokasi tidak tersedia"); return; }
    navigator.geolocation.getCurrentPosition(
      (pos) => pick(pos.coords.latitude, pos.coords.longitude),
      () => toast.error("Gagal mendapatkan lokasi"),
    );
  };

  const applyUrl = () => {
    const parsed = parseGoogleMapsUrl(urlInput.trim());
    if (!parsed) {
      if (/maps\.app\.goo\.gl|goo\.gl\/maps/.test(urlInput)) {
        toast.error("Buka dulu link pendek di browser lalu salin URL lengkapnya");
      } else {
        toast.error("Tidak menemukan koordinat di URL. Salin URL lengkap dari Google Maps.");
      }
      return;
    }
    pick(parsed.lat, parsed.lng);
    setUrlInput("");
    toast.success("Lokasi diset dari link Google Maps");
  };

  const clear = () => onChange({ lat: "", lng: "" });

  return (
    <div className="space-y-3" data-testid="location-picker">
      <div className="rounded-2xl overflow-hidden border border-bizma-border">
        <div className="h-64">
          <MapContainer center={position || MAGELANG} zoom={position ? 15 : 12} style={{ height: "100%", width: "100%" }} scrollWheelZoom>
            <TileLayer attribution='&copy; OpenStreetMap contributors' url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
            <ClickHandler onPick={pick} />
            {position && <Marker position={position} icon={icon} />}
            {position && <Recenter position={position} />}
          </MapContainer>
        </div>
        <div className="bg-bizma-muted px-4 py-2 text-xs text-bizma-subtext flex items-center gap-2">
          <MapPin size={12}/> Klik langsung di peta untuk memilih lokasi
        </div>
      </div>

      <div className="flex flex-col sm:flex-row gap-2">
        <div className="relative flex-1">
          <Link2 className="absolute left-3 top-1/2 -translate-y-1/2 text-bizma-subtext" size={16}/>
          <input value={urlInput} onChange={(e) => setUrlInput(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); applyUrl(); } }}
            placeholder="Tempel link Google Maps..." className="input-bizma pl-10" data-testid="gmaps-url-input" />
        </div>
        <button type="button" onClick={applyUrl} className="btn-outline text-sm py-2 px-4" data-testid="gmaps-url-apply">
          Ambil dari Link
        </button>
      </div>

      <div className="flex flex-wrap items-center gap-2 text-xs">
        <button type="button" onClick={useMyLocation} className="btn-outline text-xs py-1.5 px-3" data-testid="use-my-location-btn">
          <LocateFixed size={12}/> Gunakan lokasi saya
        </button>
        {hasCoords && (
          <>
            <span className="inline-flex items-center gap-1 rounded-full bg-bizma-sage/10 text-bizma-sage px-3 py-1 font-medium" data-testid="picked-coords">
              <MapPin size={12}/> {parseFloat(lat).toFixed(5)}, {parseFloat(lng).toFixed(5)}
            </span>
            <button type="button" onClick={clear} className="text-bizma-subtext hover:text-bizma-terracotta text-xs" data-testid="clear-location-btn">
              Hapus
            </button>
          </>
        )}
        {!hasCoords && <span className="text-bizma-subtext">Belum ada lokasi terpilih (opsional)</span>}
      </div>
    </div>
  );
}
